import { ErrorHandler, inject, Injectable } from '@angular/core';
import { ClientDiagnosticsService } from 'O3_core';
import * as Sentry from '@sentry/angular';

/**
 * Records unhandled errors into the ticket diagnostics buffer, then **delegates
 * to Sentry**.
 *
 * Delegation is the point: replacing `Sentry.createErrorHandler()` outright
 * would silently drop error reporting for the whole app, which is a far worse
 * regression than the feature is worth. This wrapper is additive — Sentry sees
 * exactly what it saw before.
 */
@Injectable()
export class DiagnosticsErrorHandler implements ErrorHandler {
  private readonly diagnostics = inject(ClientDiagnosticsService);
  private readonly sentryHandler = Sentry.createErrorHandler({
    showDialog: false,
  });

  handleError(error: unknown): void {
    try {
      const message =
        error instanceof Error
          ? `${error.name}: ${error.message}`
          : String(error ?? 'Unknown error');
      // Sanitised on the way into the buffer by ClientDiagnosticsService.
      this.diagnostics.recordClientError(message);
    } catch {
      // Recording diagnostics must never prevent the error from reaching Sentry.
    }

    this.sentryHandler.handleError(error);
  }
}
